import { useState, useEffect, useCallback } from "react";
import { marketingAnalytics, integrations as integrationsApi, reportOps } from "../lib/supabase.js";
import { GLOSSARY } from "./FunnelOverview.jsx";

// ═══ Marketing Analytics — date-searchable, full GA4 + GSC coverage ═══

const fmtNum = (n) => { const v = parseFloat(n) || 0; return v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString(); };
const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(1)}%`;
const fmtCtr = (n) => `${((parseFloat(n) || 0) * 100).toFixed(1)}%`;
const fmtDur = (s) => { const v = parseFloat(s) || 0; const m = Math.floor(v / 60); return m > 0 ? `${m}m ${Math.round(v % 60)}s` : `${Math.round(v)}s`; };
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => iso(new Date(Date.now() - n * 86400000));

const PRESETS = [
  { label: "7 days", days: 7 }, { label: "28 days", days: 28 },
  { label: "90 days", days: 90 }, { label: "12 months", days: 365 },
];

export default function MarketingTab({ days = 28, color = "#38bdf8" }) {
  const [start, setStart] = useState(daysAgo(days));
  const [end, setEnd] = useState(iso(new Date()));
  const [preset, setPreset] = useState(days);
  const [subTab, setSubTab] = useState("overview");
  const [showGlossary, setShowGlossary] = useState(false);
  const [analysis, setAnalysis] = useState(null);       // { label, sections } | null
  const [analyzing, setAnalyzing] = useState(null);     // label while running

  const runAnalysis = async (ids, label) => {
    setAnalyzing(label); setAnalysis(null);
    const payload = ids ? { start, end, metrics: ids } : { start, end };
    const d = await reportOps("analyze", payload);
    setAnalyzing(null);
    if (d.error) setAnalysis({ label, error: d.error });
    else setAnalysis({ label, sections: d.analysis || [] });
  };
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState({});

  const card = { background: "#12121c", border: "1px solid #1e1e2e", borderRadius: 10, padding: 18 };
  const th = { fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #1e1e2e" };
  const td = { fontSize: 12, color: "#ccc", padding: "8px 10px", borderBottom: "1px solid #16161f" };

  const load = useCallback(async (s, e) => {
    setLoading(true);
    const bd = (dim, limit) => marketingAnalytics.ga4BreakdownRange(s, e, dim, limit);
    const gbd = (dim, limit) => marketingAnalytics.gscBreakdownRange(s, e, dim, limit);
    const [ga4, ga4Daily, channels, sources, landing, pages, devices, geo, events, visitors,
           gsc, gscDaily, queries, gscPages, gscDevices, gscCountries] = await Promise.all([
      marketingAnalytics.ga4SummaryRange(s, e), marketingAnalytics.ga4DailyRange(s, e),
      bd("channel", 10), marketingAnalytics.ga4SourcesRange(s, e, 50), bd("landing_page", 100),
      bd("page_path", 100), bd("device_category", 4), bd("country", 25), bd("event_name", 50), bd("visitor_type", 3),
      marketingAnalytics.gscSummaryRange(s, e), marketingAnalytics.gscDailyRange(s, e),
      gbd("query", 100), gbd("page", 50), gbd("device", 4), gbd("country", 25),
    ]);
    const one = (r) => (Array.isArray(r.data) ? r.data[0] : r.data) || {};
    const many = (r) => r.data || [];
    setD({
      ga4: one(ga4), ga4Daily: many(ga4Daily), channels: many(channels), sources: many(sources),
      landing: many(landing), pages: many(pages), devices: many(devices), geo: many(geo),
      events: many(events), visitors: many(visitors),
      gsc: one(gsc), gscDaily: many(gscDaily), queries: many(queries), gscPages: many(gscPages),
      gscDevices: many(gscDevices), gscCountries: many(gscCountries),
    });
    setLoading(false);
  }, []);

  useEffect(() => { load(start, end); }, []); // eslint-disable-line

  const applyPreset = (p) => { const s = daysAgo(p.days); const e = iso(new Date()); setPreset(p.days); setStart(s); setEnd(e); load(s, e); };
  const applyCustom = () => { setPreset(null); load(start, end); };

  const siteOrigin = ((d.gscPages?.[0]?.dim_value || d.queries?.[0]?.page || "").match(/^https?:\/\/[^\/]+/) || [null])[0];
  const hasGA4 = (parseFloat(d.ga4?.sessions) || 0) > 0 || d.ga4Daily?.length > 0;
  const hasGSC = (parseFloat(d.gsc?.impressions) || 0) > 0 || d.gscDaily?.length > 0;

  return (
    <>
      {/* ── Date range control ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {PRESETS.map(p => (
          <button key={p.days} onClick={() => applyPreset(p)} style={{
            padding: "7px 13px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            background: preset === p.days ? `${color}18` : "transparent", color: preset === p.days ? color : "#777",
            border: `1px solid ${preset === p.days ? `${color}50` : "#1e1e2e"}` }}>{p.label}</button>
        ))}
        <div style={{ width: 1, height: 20, background: "#1e1e2e", margin: "0 4px" }} />
        <input type="date" value={start} max={end} onChange={e => setStart(e.target.value)} style={dateInp(color)} />
        <span style={{ color: "#555", fontSize: 12 }}>to</span>
        <input type="date" value={end} min={start} max={iso(new Date())} onChange={e => setEnd(e.target.value)} style={dateInp(color)} />
        <button onClick={applyCustom} style={{ padding: "7px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: color, color: "#0a0a12", border: "none" }}>Apply</button>
        {loading && <span style={{ fontSize: 11, color: "#666" }}>Loading&hellip;</span>}
        <div style={{ flex: 1 }} />
        <button onClick={() => runAnalysis(null, "everything")} style={{ background: "#8b8bd018", border: "1px solid #8b8bd050", color: "#8b8bd0", borderRadius: 6, padding: "7px 13px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Analyze everything</button>
        <button onClick={() => setShowGlossary(g => !g)} style={{ background: "none", border: "none", color: "#777", fontSize: 11, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit" }}>What do these terms mean?</button>
      </div>
      {showGlossary && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 28px" }}>
            {Object.entries(GLOSSARY).map(([term, def]) => (
              <div key={term} style={{ fontSize: 12, lineHeight: 1.55 }}>
                <span style={{ fontWeight: 700, color: "#ddd" }}>{term}</span> <span style={{ color: "#888" }}>&mdash; {def}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Analysis panel ── */}
      {(analyzing || analysis) && (
        <div style={{ ...card, marginBottom: 16, borderColor: "#33334a" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <span style={{ fontSize: 10, color: "#8b8bd0", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
              {analyzing ? `Analyzing ${analyzing}…` : `Analysis · ${analysis.label}`}
            </span>
            {analysis && <button onClick={() => setAnalysis(null)} style={{ background: "none", border: "none", color: "#666", fontSize: 11, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Dismiss</button>}
          </div>
          {analyzing && <div style={{ fontSize: 12, color: "#888" }}>Reading the data and thinking &mdash; about ten seconds.</div>}
          {analysis?.error && <div style={{ fontSize: 12, color: "#f87171" }}>{analysis.error}</div>}
          {analysis?.sections?.map((sec, i) => (
            <div key={i} style={{ marginBottom: i < analysis.sections.length - 1 ? 14 : 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#ddd", marginBottom: 4 }}>{sec.title}</div>
              <div style={{ fontSize: 12.5, color: "#aaa", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{sec.content}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Sub-tabs ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {[{ key: "overview", label: "Overview" }, { key: "traffic", label: "Website Traffic" }, { key: "search", label: "Search Console" }].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            padding: "7px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            background: subTab === t.key ? `${color}15` : "transparent", color: subTab === t.key ? color : "#555",
            border: `1px solid ${subTab === t.key ? `${color}40` : "#1e1e2e"}` }}>{t.label}</button>
        ))}
      </div>

      {!loading && !hasGA4 && !hasGSC && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 13, color: "#888", lineHeight: 1.7 }}>
            No marketing data in this range yet. Run a sync from Settings &rarr; Integrations —
            the sync response now reports each Google report individually, so if anything comes back empty you'll see exactly which report and why.
          </div>
        </div>
      )}

      {/* ═══ OVERVIEW ═══ */}
      {subTab === "overview" && (hasGA4 || hasGSC) && (<>
        <StatRow cards={[
          { label: "Sessions", value: fmtNum(d.ga4?.sessions), c: "#38bdf8" },
          { label: "Visitors", value: fmtNum(d.ga4?.users), c: "#38bdf8" },
          { label: "Pageviews", value: fmtNum(d.ga4?.pageviews), c: "#38bdf8" },
          { label: "New Users", value: fmtNum(d.ga4?.new_users), c: "#38bdf8" },
          { label: "Engagement", value: d.ga4?.avg_engagement_rate != null ? fmtPct(parseFloat(d.ga4.avg_engagement_rate) * 100) : "\u2014", c: "#22c55e" },
        ]} card={card} />
        <StatRow cards={[
          { label: "Organic Clicks", value: fmtNum(d.gsc?.clicks), c: "#22c55e" },
          { label: "Impressions", value: fmtNum(d.gsc?.impressions), c: "#a78bfa" },
          { label: "CTR", value: d.gsc?.avg_ctr != null ? fmtCtr(d.gsc.avg_ctr) : "\u2014", c: "#a78bfa" },
          { label: "Avg Position", value: d.gsc?.avg_position ? parseFloat(d.gsc.avg_position).toFixed(1) : "\u2014", c: "#f59e0b" },
          { label: "Bounce Rate", value: d.ga4?.avg_bounce_rate != null ? fmtPct(parseFloat(d.ga4.avg_bounce_rate) * 100) : "\u2014", c: "#ef4444" },
        ]} card={card} />
        <div style={{ ...card, marginBottom: 16 }}>
          <SectionTitle onAnalyze={() => runAnalysis(["ga4_daily", "gsc_daily", "funnel"], "the daily trend")}>Sessions &amp; organic clicks by day</SectionTitle>
          <DualTrend a={d.ga4Daily || []} aKey="sessions" aColor="#38bdf8" b={d.gscDaily || []} bKey="clicks" bColor="#22c55e" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={card}><SectionTitle onAnalyze={() => runAnalysis(["channels", "ga4_summary"], "traffic channels")}>Channels</SectionTitle><HBars rows={d.channels} labelKey="dim_value" valueKey="sessions" color="#38bdf8" fmt={fmtNum} /></div>
          <div style={card}><SectionTitle onAnalyze={() => runAnalysis(["visitor_types", "ga4_summary"], "new vs returning visitors")}>New vs returning</SectionTitle><HBars rows={d.visitors} labelKey="dim_value" valueKey="sessions" color="#a78bfa" fmt={fmtNum} /></div>
        </div>
      </>)}

      {/* ═══ WEBSITE TRAFFIC (GA4) ═══ */}
      {subTab === "traffic" && hasGA4 && (<>
        <div style={{ ...card, marginBottom: 16 }}>
          <SectionTitle onAnalyze={() => runAnalysis(["ga4_daily", "ga4_summary"], "daily traffic")}>Daily sessions</SectionTitle>
          <DualTrend a={d.ga4Daily || []} aKey="sessions" aColor="#38bdf8" b={d.ga4Daily || []} bKey="users" bColor="#818cf8" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div style={card}>
            <SectionTitle onAnalyze={() => runAnalysis(["sources", "channels"], "traffic sources")}>Traffic sources</SectionTitle>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Source / medium</th><th style={{ ...th, textAlign: "right" }}>Sessions</th><th style={{ ...th, textAlign: "right" }}>Bounce</th></tr></thead>
              <Paged rows={d.sources || []} per={8}>{(rows) => (
              <tbody>{rows.map((r, i) => (
                <tr key={i}><td style={td}>{r.source} <span style={{ color: "#555" }}>/ {r.medium}</span></td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.sessions)}</td><td style={{ ...td, textAlign: "right" }}>{r.avg_bounce_rate != null ? fmtPct(parseFloat(r.avg_bounce_rate) * 100) : "\u2014"}</td></tr>
              ))}</tbody>)}</Paged>
            </table>
          </div>
          <div style={card}>
            <SectionTitle onAnalyze={() => runAnalysis(["landing_pages", "ga4_summary"], "landing pages")}>Landing pages</SectionTitle>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Page</th><th style={{ ...th, textAlign: "right" }}>Sessions</th><th style={{ ...th, textAlign: "right" }}>Avg time</th></tr></thead>
              <Paged rows={d.landing || []} per={8}>{(rows) => (
              <tbody>{rows.map((r, i) => (
                <tr key={i}><td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><PageLink path={r.dim_value} origin={siteOrigin} /></td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.sessions)}</td><td style={{ ...td, textAlign: "right" }}>{fmtDur(r.avg_session_duration)}</td></tr>
              ))}</tbody>)}</Paged>
            </table>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div style={card}>
            <SectionTitle onAnalyze={() => runAnalysis(["top_pages", "ga4_summary"], "most viewed pages")}>Most viewed pages</SectionTitle>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Page</th><th style={{ ...th, textAlign: "right" }}>Views</th><th style={{ ...th, textAlign: "right" }}>Users</th></tr></thead>
              <Paged rows={d.pages || []} per={8}>{(rows) => (
              <tbody>{rows.map((r, i) => (
                <tr key={i}><td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><PageLink path={r.dim_value} origin={siteOrigin} /></td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.pageviews)}</td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.users)}</td></tr>
              ))}</tbody>)}</Paged>
            </table>
          </div>
          <div>
            <div style={{ ...card, marginBottom: 16 }}><SectionTitle>Devices</SectionTitle><HBars rows={d.devices} labelKey="dim_value" valueKey="sessions" color="#f59e0b" fmt={fmtNum} /></div>
            <div style={card}><SectionTitle>Countries</SectionTitle><HBars rows={d.geo} labelKey="dim_value" valueKey="sessions" color="#22c55e" fmt={fmtNum} /></div>
          </div>
        </div>
        <div style={card}>
          <SectionTitle onAnalyze={() => runAnalysis(["events"], "site interactions")}>Events</SectionTitle>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Event</th><th style={{ ...th, textAlign: "right" }}>Count</th><th style={{ ...th, textAlign: "right" }}>Users</th></tr></thead>
            <Paged rows={d.events || []} per={10}>{(rows) => (
            <tbody>{rows.map((r, i) => (
              <tr key={i}><td style={td}>{r.dim_value}</td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.event_count)}</td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.users)}</td></tr>
            ))}</tbody>)}</Paged>
          </table>
        </div>
      </>)}

      {/* ═══ SEARCH CONSOLE ═══ */}
      {subTab === "search" && hasGSC && (<>
        <div style={{ ...card, marginBottom: 16 }}>
          <SectionTitle onAnalyze={() => runAnalysis(["gsc_daily", "gsc_summary"], "the search trend")}>Clicks &amp; impressions by day</SectionTitle>
          <DualTrend a={d.gscDaily || []} aKey="clicks" aColor="#22c55e" b={d.gscDaily || []} bKey="impressions" bColor="#a78bfa" />
        </div>
        <div style={{ ...card, marginBottom: 16 }}>
          <SectionTitle onAnalyze={() => runAnalysis(["gsc_queries", "gsc_summary"], "search queries")}>Top search queries</SectionTitle>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Query</th><th style={{ ...th, textAlign: "right" }}>Clicks</th><th style={{ ...th, textAlign: "right" }}>Impressions</th><th style={{ ...th, textAlign: "right" }}>CTR</th><th style={{ ...th, textAlign: "right" }}>Position</th></tr></thead>
            <Paged rows={d.queries || []} per={12}>{(rows) => (
            <tbody>{rows.map((r, i) => (
              <tr key={i}><td style={td}>{r.dim_value}</td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.clicks)}</td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.impressions)}</td><td style={{ ...td, textAlign: "right" }}>{fmtCtr(r.avg_ctr)}</td><td style={{ ...td, textAlign: "right" }}>{parseFloat(r.avg_position || 0).toFixed(1)}</td></tr>
            ))}</tbody>)}</Paged>
          </table>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={card}>
            <SectionTitle onAnalyze={() => runAnalysis(["gsc_pages", "gsc_queries"], "pages in search")}>Top pages in search</SectionTitle>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Page</th><th style={{ ...th, textAlign: "right" }}>Clicks</th><th style={{ ...th, textAlign: "right" }}>Position</th></tr></thead>
              <Paged rows={d.gscPages || []} per={8}>{(rows) => (
              <tbody>{rows.map((r, i) => (
                <tr key={i}><td style={{ ...td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><PageLink path={r.dim_value} origin={siteOrigin} /></td><td style={{ ...td, textAlign: "right" }}>{fmtNum(r.clicks)}</td><td style={{ ...td, textAlign: "right" }}>{parseFloat(r.avg_position || 0).toFixed(1)}</td></tr>
              ))}</tbody>)}</Paged>
            </table>
          </div>
          <div>
            <div style={{ ...card, marginBottom: 16 }}><SectionTitle>Search by device</SectionTitle><HBars rows={d.gscDevices} labelKey="dim_value" valueKey="clicks" color="#f59e0b" fmt={fmtNum} /></div>
            <div style={card}><SectionTitle>Search by country</SectionTitle><HBars rows={d.gscCountries} labelKey="dim_value" valueKey="clicks" color="#38bdf8" fmt={fmtNum} /></div>
          </div>
        </div>
      </>)}
    </>
  );
}

const dateInp = (color) => ({ background: "#12121c", border: "1px solid #1e1e2e", borderRadius: 6, color: "#ccc", fontSize: 11.5, padding: "6px 9px", fontFamily: "inherit", colorScheme: "dark", outline: "none" });

function SectionTitle({ children, onAnalyze }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{children}</div>
      {onAnalyze && (
        <button onClick={onAnalyze} style={{ background: "none", border: "1px solid #2a2a3e", color: "#8b8bd0", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em" }}>ANALYZE</button>
      )}
    </div>
  );
}

// Client-side pagination over pre-fetched rows; renders children(pageRows)
function Paged({ rows = [], per = 10, children }) {
  const [page, setPage] = useState(0);
  const total = Math.ceil(rows.length / per) || 1;
  const cur = Math.min(page, total - 1);
  const slice = rows.slice(cur * per, cur * per + per);
  return (
    <>
      {children(slice)}
      {rows.length > per && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 10 }}>
          <button disabled={cur === 0} onClick={() => setPage(cur - 1)} style={pagerBtn(cur === 0)}>&larr; Prev</button>
          <span style={{ fontSize: 10.5, color: "#666" }}>{cur + 1} / {total} &middot; {rows.length} rows</span>
          <button disabled={cur >= total - 1} onClick={() => setPage(cur + 1)} style={pagerBtn(cur >= total - 1)}>Next &rarr;</button>
        </div>
      )}
    </>
  );
}
const pagerBtn = (off) => ({ background: "none", border: "1px solid #1e1e2e", color: off ? "#444" : "#999", borderRadius: 6, padding: "4px 10px", fontSize: 10.5, cursor: off ? "default" : "pointer", fontFamily: "inherit" });

// Clickable page links: origin inferred from GSC's full URLs
function PageLink({ path, origin }) {
  const p = String(path || "");
  const href = /^https?:\/\//.test(p) ? p : origin && p.startsWith("/") ? origin + p : null;
  const display = p.replace(/^https?:\/\/[^\/]+/, "") || "/";
  if (!href) return <>{display}</>;
  return <a href={href} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc", textDecoration: "none" }}>{display}</a>;
}

function StatRow({ cards, card }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cards.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{ ...card, padding: "16px 18px" }}>
          <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: c.c, lineHeight: 1 }}>{c.value ?? "\u2014"}</div>
        </div>
      ))}
    </div>
  );
}

// Two series over the same date axis: bars (a) + line (b), independent scales.
function DualTrend({ a, aKey, aColor, b, bKey, bColor }) {
  const W = 860, H = 180, pad = 4;
  const dates = [...new Set([...(a || []).map(r => r.metric_date), ...(b || []).map(r => r.metric_date)])].sort();
  if (!dates.length) return <div style={{ fontSize: 12, color: "#555", padding: 20 }}>No data in range.</div>;
  const aMap = Object.fromEntries((a || []).map(r => [r.metric_date, parseFloat(r[aKey]) || 0]));
  const bMap = Object.fromEntries((b || []).map(r => [r.metric_date, parseFloat(r[bKey]) || 0]));
  const aMax = Math.max(1, ...dates.map(dt => aMap[dt] || 0));
  const bMax = Math.max(1, ...dates.map(dt => bMap[dt] || 0));
  const bw = Math.max(2, Math.min(18, (W - pad * 2) / dates.length - 2));
  const x = (i) => pad + (i / Math.max(1, dates.length - 1)) * (W - pad * 2 - bw);
  const linePts = dates.map((dt, i) => `${x(i) + bw / 2},${H - 18 - ((bMap[dt] || 0) / bMax) * (H - 40)}`).join(" ");
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {dates.map((dt, i) => {
          const h = ((aMap[dt] || 0) / aMax) * (H - 40);
          return <rect key={dt} x={x(i)} y={H - 18 - h} width={bw} height={Math.max(1, h)} rx={2} fill={aColor} opacity={0.75}><title>{dt}: {aMap[dt] || 0}</title></rect>;
        })}
        <polyline points={linePts} fill="none" stroke={bColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <text x={pad} y={H - 4} fontSize="9" fill="#555">{dates[0]}</text>
        <text x={W - pad} y={H - 4} fontSize="9" fill="#555" textAnchor="end">{dates[dates.length - 1]}</text>
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
        <span style={{ fontSize: 10, color: "#888" }}><span style={{ display: "inline-block", width: 9, height: 9, background: aColor, borderRadius: 2, marginRight: 5, verticalAlign: "middle", opacity: 0.75 }} />{aKey}</span>
        <span style={{ fontSize: 10, color: "#888" }}><span style={{ display: "inline-block", width: 9, height: 3, background: bColor, marginRight: 5, verticalAlign: "middle" }} />{bKey}</span>
      </div>
    </div>
  );
}

function HBars({ rows = [], labelKey, valueKey, color, fmt }) {
  if (!rows.length) return <div style={{ fontSize: 12, color: "#555" }}>No data in range.</div>;
  const max = Math.max(1, ...rows.map(r => parseFloat(r[valueKey]) || 0));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r, i) => {
        const v = parseFloat(r[valueKey]) || 0;
        return (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 3 }}>
              <span style={{ color: "#bbb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}>{r[labelKey] || "(not set)"}</span>
              <span style={{ color: "#777" }}>{fmt(v)}</span>
            </div>
            <div style={{ height: 5, background: "#16161f", borderRadius: 3 }}>
              <div style={{ height: "100%", width: `${(v / max) * 100}%`, background: color, borderRadius: 3, opacity: 0.8 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
